import {OWN_CLUB_LOGO_URL} from '../config';
import {ClubInfo, ClubsConfig, TableRow} from '../types';
import {isOwnTeam} from './games';

/** "SK Zadruga Aich/Dob/2" and "SK Zadruga Aich/Dob 2" name the same team. */
const normalize = (name: string) => name.toLowerCase().replace(/\/(\d+)$/, ' $1').replace(/\s+/g, ' ').trim();

const indexCache = new WeakMap<ClubsConfig, Map<string, ClubInfo>>();

/** All clubs of all leagues, reachable by club name and by table name. */
function clubIndex(clubs: ClubsConfig): Map<string, ClubInfo> {
    let index = indexCache.get(clubs);
    if (!index) {
        index = new Map();
        for (const league of Object.values(clubs)) {
            for (const [clubName, info] of Object.entries(league ?? {})) {
                for (const alias of [clubName, info.team_table_name]) {
                    const key = normalize(alias ?? '');
                    const existing = index.get(key);
                    // the same club appears in several leagues – prefer an entry that has a logo
                    if (key && (!existing || (!existing.logo && info.logo))) {
                        index.set(key, info);
                    }
                }
            }
        }
        indexCache.set(clubs, index);
    }
    return index;
}

/** Finds a club by name; unless `exactOnly` is set, a club whose name contains `name` also matches. */
export function findClub(clubs: ClubsConfig, name: string, exactOnly = false): ClubInfo | undefined {
    const key = normalize(name);
    if (!key) return undefined;

    const index = clubIndex(clubs);
    const exact = index.get(key);
    if (exact || exactOnly) return exact;

    for (const [alias, info] of index) {
        if (alias.includes(key)) return info;
    }
    return undefined;
}

export function clubLogoUrl(clubs: ClubsConfig, team: string): string | undefined {
    return isOwnTeam(team) ? OWN_CLUB_LOGO_URL : findClub(clubs, team)?.logo || undefined;
}

/** Applies the names and links maintained in the "clubs" config to a volleynet table. */
export function applyClubOverrides(rows: TableRow[], clubs: ClubsConfig, league: string): TableRow[] {
    return rows.map(row => {
        const club = clubs[league]?.[row.name] ?? findClub(clubs, row.name, true);
        if (!club) return row;
        return {
            ...row,
            name: club.team_table_name || row.name,
            shortName: club.team_table_name_short || row.shortName,
            link: club.team_link || undefined,
        };
    });
}
